import { useEffect, useState } from "react";
import { Replica, compareBookings, computeState, demoData, extendState, mergeEntity, optionsFromEntities, toSnapshot, uuidv7 } from "@lagerlog/core";
import type { Booking, Entity, LoadResponse } from "@lagerlog/core";

/**
 * Measurements in this browser (P0-6): a year of the invented wine bar, computed here.
 * #/messung?auto runs at once and writes the results as JSON into #results (for Playwright).
 */
type Results = Record<string, number>;

const DAY = 86_400_000;
const pause = () => new Promise((r) => setTimeout(r, 0));

function median(fn: () => void, runs = 5): number {
  const times: number[] = [];
  for (let i = 0; i < runs; i++) {
    const t = performance.now();
    fn();
    times.push(performance.now() - t);
  }
  times.sort((a, b) => a - b);
  return Math.round(times[Math.floor(times.length / 2)]! * 10) / 10;
}

async function run(log: (s: string) => void): Promise<Results> {
  const end = Math.floor(Date.now() / DAY) * DAY;
  log("Erzeuge ein Gastro-Jahr …");
  await pause();
  let t = performance.now();
  const data = demoData({ start: end - 365 * DAY, days: 365 });
  const res: Results = { bookings: data.bookings.length, generateMs: Math.round(performance.now() - t) };
  const entities = new Map<string, Entity>();
  for (const c of data.entities) entities.set(`${c.type}|${c.id}`, mergeEntity(entities.get(`${c.type}|${c.id}`), c));
  const opts = optionsFromEntities(entities.values());
  const sorted = [...data.bookings].sort(compareBookings);

  log("Ganzes Jahr berechnen …");
  await pause();
  res.fullYearMs = median(() => computeState(sorted, { ...opts, sorted: true }));

  log("Stichtagsstand + 30 Tage …");
  await pause();
  const cutoff = end - 30 * DAY;
  const snapshot = toSnapshot(computeState(sorted.filter((b) => b.at <= cutoff), { ...opts, sorted: true }), cutoff);
  const after = sorted.filter((b) => b.at > cutoff);
  res.snapshotPlus30Ms = median(() => computeState(after, { ...opts, sorted: true, from: snapshot }));

  log("Erstes Laden auf dem Gerät (JSON lesen + berechnen) …");
  await pause();
  const load: LoadResponse = { snapshot, snapshotSeq: 1, bookings: after, entities: [...entities.values()], seq: 2, resolutions: {}, resolutionsTag: "" };
  const json = JSON.stringify(load);
  res.loadJsonKB = Math.round(json.length / 1000);
  res.loadClientMs = median(() => {
    const r = new Replica(["keller", "bar", "kueche"]);
    r.applyLoad(JSON.parse(json) as LoadResponse);
    r.state();
  });

  log("Neue Buchung anhängen …");
  await pause();
  const state = computeState(sorted, { ...opts, sorted: true });
  let at = end + 1000;
  t = performance.now();
  for (let i = 0; i < 1000; i++) {
    const b: Booking = { id: uuidv7(at), at, recordedAt: at, kind: "out", article: "wein-3", lager: "keller", location: "keller/regal-a", qty: 750, reason: "sale" };
    at += 1000;
    extendState(state, [b], opts);
  }
  res.appendUs = Math.round(((performance.now() - t) / 1000) * 1000);
  return res;
}

export function Measure() {
  const [log, setLog] = useState<string[]>([]);
  const [results, setResults] = useState<Results | null>(null);
  const start = async () => {
    setResults(null);
    setResults(await run((s) => setLog((l) => [...l, s])));
  };
  useEffect(() => {
    if (location.hash.includes("auto")) void start();
  }, []);
  return (
    <div className="measure">
      <h1>Messung im Browser</h1>
      <p className="muted">Ein erfundenes Gastro-Jahr (~110 000 Buchungen) wird auf diesem Gerät erzeugt und berechnet. Dauert einige Sekunden.</p>
      <button className="primary" onClick={start}>
        Messen
      </button>
      <ul>{log.map((l, i) => <li key={i}>{l}</li>)}</ul>
      {results && (
        <table>
          <tbody>
            <tr><th>Buchungen</th><td>{results.bookings}</td></tr>
            <tr><th>Ganzes Jahr berechnen</th><td>{results.fullYearMs} ms</td></tr>
            <tr><th>Stichtagsstand + 30 Tage</th><td>{results.snapshotPlus30Ms} ms</td></tr>
            <tr><th>Erstes Laden: JSON lesen + berechnen ({results.loadJsonKB} kB)</th><td>{results.loadClientMs} ms</td></tr>
            <tr><th>Neue Buchung anhängen</th><td>{results.appendUs} µs</td></tr>
          </tbody>
        </table>
      )}
      <pre id="results">{results ? JSON.stringify(results) : ""}</pre>
      <a href="#/status">Zurück</a>
    </div>
  );
}
